import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RFQDetails = ({ rfq, onStatusUpdate, onAssign, onAddNote }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [newNote, setNewNote] = useState('');
  const [assignee, setAssignee] = useState(rfq?.assignedTo || '');

  const statusOptions = [
    { value: 'pending', label: 'Pending Review' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'quoted', label: 'Quote Sent' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const staffOptions = [
    { value: 'john_doe', label: 'John Doe - Senior Consultant' },
    { value: 'jane_smith', label: 'Jane Smith - Infrastructure Specialist' },
    { value: 'mike_wilson', label: 'Mike Wilson - Software Architect' },
    { value: 'sarah_johnson', label: 'Sarah Johnson - Network Engineer' }
  ];

  const tabs = [
    { id: 'details', label: 'Details', icon: 'FileText' },
    { id: 'requirements', label: 'Requirements', icon: 'CheckSquare' },
    { id: 'history', label: 'History', icon: 'Clock' },
    { id: 'documents', label: 'Documents', icon: 'Paperclip' }
  ];

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const handleAddNote = () => {
    if (newNote?.trim()) {
      onAddNote(rfq?.id, newNote);
      setNewNote('');
    }
  };

  const handleAssign = () => {
    if (assignee) {
      onAssign(rfq?.id, assignee);
    }
  };

  if (!rfq) {
    return (
      <div className="flex items-center justify-center h-full bg-card rounded-lg border border-border">
        <div className="text-center">
          <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Select an RFQ</h3>
          <p className="text-muted-foreground">Choose an RFQ from the list to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-1">RFQ #{rfq?.id}</h2>
            <p className="text-muted-foreground">{rfq?.title}</p>
          </div>
          <div className="flex items-center space-x-2">
            <Select
              options={statusOptions}
              value={rfq?.status}
              onChange={(value) => onStatusUpdate(rfq?.id, value)}
              className="w-40"
            />
            <Button variant="outline" iconName="Download" iconPosition="left">
              Export
            </Button>
          </div>
        </div>

        {/* Customer Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Customer</p>
            <p className="font-medium text-foreground">{rfq?.customer?.name}</p>
            <p className="text-sm text-muted-foreground">{rfq?.customer?.email}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Company</p>
            <p className="font-medium text-foreground">{rfq?.customer?.company}</p>
            <p className="text-sm text-muted-foreground">{rfq?.customer?.phone}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Submitted</p>
            <p className="font-medium text-foreground">{formatDate(rfq?.createdAt)}</p>
            <p className="text-sm text-muted-foreground">Deadline: {formatDate(rfq?.deadline)}</p>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex space-x-8 px-6">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        {activeTab === 'details' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-foreground mb-3">Project Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Category</p>
                    <p className="text-sm text-foreground">{rfq?.category}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Priority</p>
                    <p className="text-sm text-foreground capitalize">{rfq?.priority}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Budget Range</p>
                    <p className="text-sm text-foreground">
                      {rfq?.budgetRange ? `${formatCurrency(rfq?.budgetRange?.min)} - ${formatCurrency(rfq?.budgetRange?.max)}` : 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Timeline</p>
                    <p className="text-sm text-foreground">{rfq?.timeline}</p>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-3">Assignment</h3>
                <div className="space-y-3">
                  <Select
                    label="Assign to Staff"
                    options={staffOptions}
                    value={assignee}
                    onChange={setAssignee}
                    placeholder="Select staff member"
                  />
                  <Button onClick={handleAssign} variant="outline" fullWidth>
                    Update Assignment
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-3">Description</h3>
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-foreground whitespace-pre-wrap">{rfq?.description}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'requirements' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground mb-3">Technical Requirements</h3>
            {rfq?.requirements?.map((req, index) => (
              <div key={index} className="p-4 border border-border rounded-lg">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckSquare" size={16} className="text-primary mt-1" />
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground mb-1">{req?.title}</h4>
                    <p className="text-sm text-muted-foreground mb-2">{req?.description}</p>
                    {req?.specifications && (
                      <div className="space-y-1">
                        {req?.specifications?.map((spec, specIndex) => (
                          <div key={specIndex} className="flex items-center space-x-2 text-xs text-muted-foreground">
                            <Icon name="Dot" size={12} />
                            <span>{spec}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Activity History</h3>
              <div className="flex items-center space-x-2">
                <Input
                  type="text"
                  placeholder="Add internal note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e?.target?.value)}
                  className="w-64"
                />
                <Button onClick={handleAddNote} size="sm">
                  Add Note
                </Button>
              </div>
            </div>
            
            <div className="space-y-3">
              {rfq?.history?.map((item, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 border border-border rounded-lg">
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Icon name={item?.type === 'status' ? 'Activity' : 'MessageSquare'} size={14} color="white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-foreground">{item?.message}</p>
                    <div className="flex items-center space-x-2 mt-1 text-xs text-muted-foreground">
                      <span>{item?.user}</span>
                      <Icon name="Dot" size={8} />
                      <span>{formatDate(item?.timestamp)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground">Attached Documents</h3>
              <Button variant="outline" iconName="Upload" iconPosition="left">
                Upload Document
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rfq?.documents?.map((doc, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted transition-colors">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{doc?.name}</p>
                    <p className="text-xs text-muted-foreground">{doc?.size} • {formatDate(doc?.uploadedAt)}</p>
                  </div>
                  <Button variant="ghost" size="sm" iconName="Download" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RFQDetails;